<template>
  <el-dialog
    title="欠费减免申请"
    :visible.sync="dialogVisible"
    width="1020px"
    :before-close="handleClose"
    custom-class="apply-deduction-dialog"
  >
    <div class="deduction-tip">
      <i class="el-icon-info" style="color:#E6A23C"></i>
      勾选要减免的费用，填写减免金额。欠费费用审核通过后对应欠款被清减；未欠费费用也可减免（如预先减免后续费用）。提交后由物业最高管理员审核。
    </div>

    <el-table :data="feeList" border size="small" style="width:100%" max-height="360">
      <el-table-column label="费用项目" prop="feeName" align="center" min-width="120" />
      <el-table-column label="减免时间段" align="center" width="190">
        <template #default="{row}">
          <span>{{ getPeriod(row) }}</span>
        </template>
      </el-table-column>
      <el-table-column label="费用金额" align="center" width="95">
        <template #default="{row}">
          <span>{{ parseFloat(row.feeTotalPrice || row.amountOwed || 0).toFixed(2) }}</span>
        </template>
      </el-table-column>
      <el-table-column label="当前欠款" align="center" width="95">
        <template #default="{row}">
          <span :style="parseFloat(row.amountOwed||0) > 0 ? 'color:#F56C6C;font-weight:bold' : 'color:#909399'">{{ (parseFloat(row.amountOwed) || parseFloat(row.feeTotalPrice) || 0).toFixed(2) }}</span>
        </template>
      </el-table-column>
      <el-table-column label="月单价" align="center" width="90">
        <template #default="{row}">
          <span>{{ getMonthPrice(row).toFixed(2) }}</span>
        </template>
      </el-table-column>
      <el-table-column label="减免金额" align="center" width="150">
        <template #default="{row}">
          <el-input-number v-model="row._deductionAmount" :min="0"
            :precision="2" :controls="false" size="small"
            style="width:120px" placeholder="减免金额" />
        </template>
      </el-table-column>
      <el-table-column label="≈减免月数" align="center" width="90">
        <template #default="{row}">
          <span>{{ computeMonths(row) }}</span>
        </template>
      </el-table-column>
      <el-table-column label="操作" align="center" width="70">
        <template #default="{$index}">
          <el-button type="text" size="small" style="color:#F56C6C" @click="removeRow($index)">移除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="deduction-summary">
      <span>共 {{ validRows.length }} 条，减免合计：
        <b style="color:#F56C6C">{{ totalDeduction.toFixed(2) }}</b> 元
      </span>
    </div>

    <el-form :model="form" label-width="80px" style="margin-top:12px">
      <el-form-item label="减免原因">
        <el-input v-model="form.message" type="textarea" :rows="2"
          placeholder="请填写减免原因（必填，将随申请提交给审核人）" maxlength="200" show-word-limit />
      </el-form-item>
    </el-form>

    <span slot="footer" class="dialog-footer">
      <el-button @click="dialogVisible = false">取 消</el-button>
      <el-button type="primary" :loading="submitting" @click="submit">提交申请</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { applyFeeDeduction } from '@/api/fee/feeDeductionApi'
import { getCommunityId } from '@/api/community/communityApi'
import { getUserName } from '@/api/user/userApi'
import { dateFormat } from '@/utils/dateUtil'

export default {
  name: 'ApplyFeeDeduction',
  data() {
    return {
      dialogVisible: false,
      submitting: false,
      communityId: '',
      feeList: [],
      form: {
        message: ''
      }
    }
  },
  computed: {
    // 有效行：减免金额 > 0
    validRows() {
      return this.feeList.filter(f => parseFloat(f._deductionAmount || 0) > 0)
    },
    totalDeduction() {
      return this.validRows.reduce((s, f) => s + parseFloat(f._deductionAmount || 0), 0)
    }
  },
  created() {
    this.communityId = getCommunityId()
  },
  methods: {
    /**
     * 打开弹窗
     * @param {Object} param - { fees: [欠费行], ownerName? }
     */
    open(param) {
      this.form.message = ''
      // 深拷贝欠费行，避免污染父组件数据；初始化减免金额字段
      const fees = (param && param.fees) || []
      this.feeList = fees.map(f => {
        // 预处理：确保费用金额有值（未欠费费用可能没传 feeTotalPrice）
        let feeTotalPrice = parseFloat(f.feeTotalPrice || 0)
        if (feeTotalPrice <= 0) {
          const feePrice = parseFloat(f.feePrice || 0)
          const cycle = parseFloat(f.cycle || f.paymentCycle || f.oweMonth || 1)
          if (feePrice > 0 && cycle > 0) {
            feeTotalPrice = feePrice * cycle
          }
        }
        return {
          ...f,
          feeTotalPrice: feeTotalPrice || f.receivableAmount || f.amountOwed || 0,
          _deductionAmount: parseFloat(feeTotalPrice || f.receivableAmount || f.amountOwed || 0)
        }
      })
      if (this.feeList.length === 0) {
        this.$message.warning('请先勾选费用项目')
        return
      }
      this.dialogVisible = true
    },
    // 取月单价：优先用 feePrice，其次用 feeTotalPrice/cycle 反推
    getMonthPrice(row) {
      const feePrice = parseFloat(row.feePrice || 0)
      if (feePrice > 0) return feePrice
      const owed = parseFloat(row.amountOwed || 0)
      const cycle = parseFloat(row.cycle || row.oweMonth || 1)
      if (cycle <= 0) return owed
      return owed / cycle
    },
    // 格式化月份显示
    getPeriod(row) {
      if (row.state === '2009001') return '-'
      const start = row.startTime ? dateFormat(row.startTime).substring(0, 10) : '-'
      let end = row.endTime ? dateFormat(row.endTime).substring(0, 10) : '-'
      if (start === end && row.deadlineTime) {
        end = dateFormat(row.deadlineTime).substring(0, 10)
      }
      return start === end ? start : `${start}~${end}`
    },
    // 减免金额对应的大约月数
    computeMonths(row) {
      const ded = parseFloat(row._deductionAmount || 0)
      if (ded <= 0) return '-'
      const mp = this.getMonthPrice(row)
      if (mp <= 0) return '-'
      return (ded / mp).toFixed(1) + '月'
    },
    removeRow(index) {
      this.feeList.splice(index, 1)
    },
    async submit() {
      if (this.validRows.length === 0) {
        this.$message.warning('请至少为一条欠费填写减免金额')
        return
      }
      if (!this.form.message || !this.form.message.trim()) {
        this.$message.warning('请填写减免原因')
        return
      }

      this.submitting = true
      let success = 0
      let fail = 0
      const errors = []
      for (const f of this.validRows) {
        try {
          await applyFeeDeduction({
            communityId: this.communityId,
            feeId: f.feeId,
            deductionAmount: parseFloat(f._deductionAmount).toFixed(2),
            message: this.form.message.trim(),
            applyUserName: getUserName() || ''
          })
          success++
        } catch (e) {
          fail++
          const msg = (e && (e.msg || e.message)) || '提交失败'
          errors.push(`${f.feeName}: ${msg}`)
        }
      }
      this.submitting = false

      if (success > 0 && fail === 0) {
        this.$message.success(`已提交 ${success} 条减免申请，等待管理员审核`)
        this.dialogVisible = false
        this.$emit('success')
      } else if (success > 0 && fail > 0) {
        this.$message.warning(`部分成功：成功 ${success} 条，失败 ${fail} 条。${errors.slice(0, 2).join('；')}`)
        this.$emit('success')
      } else {
        this.$message.error(errors[0] || '提交失败')
      }
    },
    handleClose(done) {
      this.dialogVisible = false
      if (done) done()
    }
  }
}
</script>

<style scoped>
.deduction-tip {
  background: #fdf6ec;
  border: 1px solid #f5dab1;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 12px;
  color: #E6A23C;
  margin-bottom: 12px;
  line-height: 1.6;
}
.deduction-summary {
  margin-top: 10px;
  text-align: right;
  font-size: 13px;
  color: #606266;
}
</style>
